/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-color-palette-generator block)");
/* eslint-enable no-console */

(function () {
    'use strict';

    /* ─── CONFIG ────────────────────────────────────────────────── */
    const STORAGE_KEY = 'acpg-v3';
    const DEFAULT = {
        baseHex: '#6366f1', harmonyMode: 'tetradic', viewMode: 'grid',
        fmtMode: 'css', contrastColor: '#ffffff', paletteCount: 4,
        tempShift: 0, lockedColors: {}, comparePalettes: []
    };
    const HARMONY_DESC = {
        complementary: 'Opposite colors on the wheel — bold contrast and clear separation.',
        analogous: 'Neighbor colors with gentle transitions and a naturally cohesive feel.',
        triadic: 'Evenly spaced colors (120°) that stay vibrant while remaining balanced.',
        tetradic: 'Two complementary pairs (90°) for richer, more varied visual systems.',
        split: 'Base color plus two neighbors of its complement — softer than a direct complement.',
        monochromatic: 'Same hue at multiple lightness levels — clean and consistent.'
    };
    const HARMONY_NAMES = {
        complementary: (i) => i === 0 ? 'Base' : i === 1 ? 'Complement' : `Accent ${i - 1}`,
        analogous: (i) => i === 0 ? 'Base' : `Analogous ${i}`,
        triadic: (i) => i === 0 ? 'Base' : `Triadic ${i}`,
        tetradic: (i) => i === 0 ? 'Base' : `Tetradic ${i}`,
        split: (i) => i === 0 ? 'Base' : `Split ${i}`,
        monochromatic: (i) => ['Darkest', 'Dark', 'Base', 'Light', 'Lightest', 'Tint 6', 'Tint 7', 'Tint 8', 'Tint 9', 'Tint 10', 'Tint 11', 'Tint 12'][i] || `Mono ${i + 1}`
    };
    const PRESETS = [
        '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#6366f1',
        '#8b5cf6', '#ec4899', '#06b6d4', '#3b82f6', '#000000', '#6b7280',
        '#a3a3a3', '#dc2626', '#d97706', '#16a34a', '#0891b2', '#4f46e5', '#7c3aed', '#db2777'
    ];

    /* ─── STATE ─────────────────────────────────────────────────── */
    const state = (() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
            return {
                ...structuredClone(DEFAULT), ...saved,
                lockedColors: saved.lockedColors || {},
                comparePalettes: saved.comparePalettes || []
            };
        } catch { return structuredClone(DEFAULT); }
    })();

    function save() { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
    function reset() { Object.assign(state, structuredClone(DEFAULT)); syncUI(); render(); }

    /* ─── DOM REFS ──────────────────────────────────────────────── */
    const $ = id => document.getElementById(id);
    const ui = {
        colorPicker: $('colorPicker'), hexInput: $('hexInput'),
        harmonyMode: $('harmonyMode'), paletteCount: $('paletteCount'),
        tempShift: $('tempShift'), tempOut: $('tempOut'),
        paletteStrip: $('paletteStrip'), paletteGrid: $('paletteGrid'), paletteList: $('paletteList'),
        harmonyDesc: $('harmonyDesc'), editIndex: $('editIndex'),
        hueRange: $('hueRange'), satRange: $('satRange'), lightRange: $('lightRange'),
        hueOut: $('hueOut'), satOut: $('satOut'), lightOut: $('lightOut'),
        hslPreviewSwatch: $('hslPreviewSwatch'), hslPreviewHex: $('hslPreviewHex'),
        previewLight: $('previewLight'), previewDark: $('previewDark'),
        compareGrid: $('compareGrid'), contrastGrid: $('contrastGrid'),
        contrastColor: $('contrastColor'), contrastHex: $('contrastHex'),
        codeOutput: $('codeOutput'), btnCopy: $('btnCopy'),
        toast: $('toast'), exportCanvas: $('exportCanvas'),
        gridView: $('gridView'), listView: $('listView')
    };

    /* ─── UTILS : Color Math ─────────────────────────────────────── */
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const rotHue = (h, d) => ((h + d) % 360 + 360) % 360;

    function hexToRgb(hex) {
        const h = hex.replace('#', '');
        const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function rgbToHex(r, g, b) {
        return '#' + [r, g, b].map(v => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('');
    }
    function rgbToHsl(r, g, b) {
        r /= 255; g /= 255; b /= 255;
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        let h = 0, s = 0, l = (mx + mn) / 2;
        if (mx !== mn) {
            const d = mx - mn;
            s = l > .5 ? d / (2 - mx - mn) : d / (mx + mn);
            h = mx === r ? (g - b) / d + (g < b ? 6 : 0)
                : mx === g ? (b - r) / d + 2
                    : (r - g) / d + 4;
            h /= 6;
        }
        return [h * 360, s * 100, l * 100];
    }
    function hslToRgb(h, s, l) {
        h /= 360; s /= 100; l /= 100;
        if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
        const q = l < .5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        const hue = (p, q, t) => {
            if (t < 0) t += 1; if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        return [hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)].map(v => Math.round(v * 255));
    }
    const hslToHex = (h, s, l) => rgbToHex(...hslToRgb(h, s, l));

    function luminance(r, g, b) {
        return [r, g, b].reduce((sum, v, i) => {
            v /= 255;
            return sum + (v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4)) * [.2126, .7152, .0722][i];
        }, 0);
    }
    function contrastRatio(h1, h2) {
        const [l1, l2] = [h1, h2].map(h => luminance(...hexToRgb(h)));
        return (Math.max(l1, l2) + .05) / (Math.min(l1, l2) + .05);
    }
    const idealText = hex => contrastRatio(hex, '#000') >= contrastRatio(hex, '#fff') ? '#000000' : '#ffffff';
    const contrastLabel = r => r >= 7 ? { text: 'AAA', color: '#166534' }
        : r >= 4.5 ? { text: 'AA', color: '#1a7a3a' }
            : r >= 3 ? { text: 'AA Large', color: '#854d0e' }
                : { text: 'Fail', color: '#c00' };

    function tempAdjust(hex, shift) {
        if (!shift) return hex;
        const [r, g, b] = hexToRgb(hex);
        const [h, s, l] = rgbToHsl(r, g, b);
        return hslToHex(rotHue(h, shift), s, l);
    }

    /* ─── UTILS : DOM ────────────────────────────────────────────── */
    const debounce = (fn, ms = 80) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
    const sanitize = s => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const showToast = msg => {
        ui.toast.textContent = msg;
        ui.toast.classList.add('show');
        setTimeout(() => ui.toast.classList.remove('show'), 1800);
    };
    const copy = (text, msg) => navigator.clipboard.writeText(text)
        .then(() => showToast(msg || `Copied: ${text}`))
        .catch(() => showToast('Copy failed'));

    /* ─── PALETTE GENERATION ─────────────────────────────────────── */
    let palette = [];

    function buildPalette() {
        const { baseHex, harmonyMode, paletteCount, tempShift, lockedColors } = state;
        const [r, g, b] = hexToRgb(baseHex);
        const [h, s, l] = rgbToHsl(r, g, b);
        const n = paletteCount;

        const strategies = {
            complementary: () => Array.from({ length: n }, (_, i) =>
                i === 0 ? baseHex : hslToHex(rotHue(h, 180), Math.max(20, s - (i - 1) * 6), clamp(l + (i - 1) * 8, 18, 85))),
            analogous: () => {
                const a = Array.from({ length: n }, (_, i) => hslToHex(rotHue(h, (i - Math.floor(n / 2)) * 20), s, l));
                a[Math.floor(n / 2)] = baseHex; return a;
            },
            triadic: () => Array.from({ length: n }, (_, i) =>
                i === 0 ? baseHex : hslToHex(rotHue(h, (360 / Math.min(3, n)) * (i % 3 || 1)), Math.max(25, s - i * 3), l)),
            tetradic: () => Array.from({ length: n }, (_, i) =>
                i === 0 ? baseHex : hslToHex(rotHue(h, (360 / n) * i), Math.max(25, s - i * 2), l)),
            split: () => Array.from({ length: n }, (_, i) => {
                if (i === 0) return baseHex;
                return hslToHex(rotHue(h, [150, 210, 180, 120, 240, 90, 270][(i - 1) % 7]), Math.max(22, s - i * 2), l);
            }),
            monochromatic: () => {
                const start = Math.max(10, l - 35), end = Math.min(90, l + 35);
                return Array.from({ length: n }, (_, i) => hslToHex(h, s, start + ((end - start) / Math.max(1, n - 1)) * i));
            }
        };

        let arr = (strategies[harmonyMode] || strategies.tetradic)().slice(0, n).map(c => tempAdjust(c, tempShift));
        Object.entries(lockedColors).forEach(([k, v]) => { if (k < arr.length) arr[k] = v; });
        palette = arr;
    }

    function getPaletteItems() {
        const nameFn = HARMONY_NAMES[state.harmonyMode] || HARMONY_NAMES.tetradic;
        return palette.map((hex, i) => {
            const [r, g, b] = hexToRgb(hex);
            return { i, hex: hex.toUpperCase(), rgb: `rgb(${r}, ${g}, ${b})`, name: nameFn(i), locked: !!state.lockedColors[i] };
        });
    }

    /* ─── RENDERERS ─────────────────────────────────────────────── */
    function renderStrip(items) {
        ui.paletteStrip.innerHTML = items.map(({ i, hex, locked }) => `
    <div class="palette-strip-color" style="background:${hex}" data-copy="${hex}" role="button" tabindex="0">
      <button class="lock-btn${locked ? ' active' : ''}" data-lock="${i}" aria-label="${locked ? 'Unlock' : 'Lock'} color ${i + 1}">${locked ? '🔒' : '🔓'}</button>
      <span class="strip-hex">${hex}</span>
    </div>`).join('');
    }

    function renderGrid(items) {
        ui.paletteGrid.innerHTML = items.map(({ hex, name }) => `
    <div class="shade-item" data-copy="${hex}" role="button" tabindex="0">
      <div class="shade-color" style="background:${hex}"></div>
      <div class="shade-label">${hex}<br><span class="shade-name">${name}</span></div>
    </div>`).join('');
    }

    function renderList(items) {
        ui.paletteList.innerHTML = items.map(({ i, hex, rgb, name }) => `
    <div class="shade-row" data-copy="${hex}" data-row="${i}" role="button" tabindex="0">
      <div class="shade-row-color" style="background:${hex}"></div>
      <div class="shade-row-hex">${hex}</div>
      <div class="shade-row-rgb">${rgb}</div>
      <div class="shade-row-name">${name}</div>
      <div class="copied-badge" id="cb${i}">Copied!</div>
    </div>`).join('');
    }

    function renderPreview() {
        const [c1, c2, c3, c4] = [palette[0], palette[1], palette[2], palette[3]].map((c, i) =>
            c || tempAdjust(state.baseHex, [0, 180, 30, 0][i]) || '#f3f4f6'
        );
        const card = (bg, body, title, subtitle, chips, h3, p, btn1, btn2, dark = false) => `
    <div class="preview-header" style="background:${bg};color:${idealText(bg)}">
      <strong>${title}</strong>
      <div style="opacity:.85;font-size:13px;margin-top:4px">${subtitle}</div>
    </div>
    <div class="preview-body" style="background:${body};color:${dark ? '#e5e7eb' : idealText(body)}">
      ${chips.map(([bg, label]) => `<div class="chip" style="background:${bg};color:${idealText(bg)}">${label}</div>`).join('')}
      <h3 style="margin:14px 0 8px;color:${h3}">${p.title}</h3>
      <p style="font-size:14px;line-height:1.6">${p.text}</p>
      <div class="preview-btns">
        ${btn1 ? `<button class="ui-btn" style="background:${btn1[0]};color:${idealText(btn1[0])}">${btn1[1]}</button>` : ''}
        ${btn2 ? `<button class="ui-btn" style="background:${btn2[0]};color:${idealText(btn2[0])}">${btn2[1]}</button>` : ''}
      </div>
    </div>`;
        ui.previewLight.innerHTML = card(c1, c4, 'Product dashboard', 'Live preview — updates with your palette',
            [[c2, 'Active'], [c3, 'New']], c1, { title: 'Interface card', text: 'Buttons, chips, headings and surfaces update instantly as your palette changes.' },
            [c1, 'Primary action'], [c2, 'Secondary']);
        ui.previewDark.innerHTML = card(c2, '#111827', 'Dark mode sample', 'Preview on dark interfaces',
            [[c1, 'Brand'], [c3, 'Accent']], c2, { title: 'Card on dark surface', text: 'Check contrast, emphasis and readability across both light and dark backgrounds.' },
            [c2, 'Continue'], [c3, 'Details'], true);
    }

    function renderCompare() {
        ui.compareGrid.innerHTML = state.comparePalettes.map((item, idx) => `
    <div class="compare-box">
      <div class="compare-head">
        <strong>${item.mode} · ${item.colors.length} colors</strong>
        <button class="btn-tool" data-del="${idx}">Remove</button>
      </div>
      <div class="mini-strip">${item.colors.map(c => `<div style="background:${c}"></div>`).join('')}</div>
      <div style="padding:12px;font-family:monospace;font-size:11px;line-height:1.7">
        ${item.colors.map(c => c.toUpperCase()).join('<br>')}
      </div>
    </div>`).join('');
    }

    /* ─── FORMATTERS ─────────────────────────────────────────────── */
    const formatters = {
        css: (p, n) => `:root {\n${p.map((h, i) => `  --color-${sanitize(n[i])}: ${h};`).join('\n')}\n}`,
        scss: (p, n) => p.map((h, i) => `$color-${sanitize(n[i])}: ${h};`).join('\n'),
        json: (p, n) => JSON.stringify(Object.fromEntries(p.map((h, i) => [sanitize(n[i]), h])), null, 2),
        array: (p, n) => `const palette = [\n${p.map((h, i) => `  "${h}" // ${n[i]}`).join(',\n')}\n];`
    };

    function renderCode() {
        const nameFn = HARMONY_NAMES[state.harmonyMode] || HARMONY_NAMES.tetradic;
        const names = palette.map((_, i) => nameFn(i));
        ui.codeOutput.value = (formatters[state.fmtMode] || formatters.css)(palette, names);
    }

    function renderContrast() {
        ui.contrastGrid.innerHTML = palette.map(hex => {
            const { text, color } = contrastLabel(contrastRatio(hex, state.contrastColor));
            const ratio = contrastRatio(hex, state.contrastColor).toFixed(1);
            return `
      <div class="shade-item" style="cursor:default">
        <div class="shade-color" style="background:${hex};display:flex;align-items:center;justify-content:center">
          <span style="color:${state.contrastColor};font-size:13px;font-weight:700">Aa</span>
        </div>
        <div class="shade-label" style="font-size:9px;line-height:1.6">
          ${ratio}:1<br><span style="color:${color};font-weight:700">${text}</span>
        </div>
      </div>`;
        }).join('');
    }

    function renderEditor() {
        ui.editIndex.innerHTML = palette.map((hex, i) =>
            `<option value="${i}">Color ${i + 1} — ${hex.toUpperCase()}</option>`
        ).join('');
        syncEditor();
    }

    function syncEditor() {
        const hex = palette[parseInt(ui.editIndex.value || 0)] || state.baseHex;
        const [h, s, l] = rgbToHsl(...hexToRgb(hex));
        ui.hueRange.value = Math.round(h); ui.hueOut.textContent = Math.round(h);
        ui.satRange.value = Math.round(s); ui.satOut.textContent = Math.round(s) + '%';
        ui.lightRange.value = Math.round(l); ui.lightOut.textContent = Math.round(l) + '%';
        updateHslPreview(); // sync le swatch au changement de couleur sélectionnée
    }

    function syncUI() {
        ui.colorPicker.value = state.baseHex;
        ui.hexInput.value = state.baseHex.replace('#', '').toUpperCase();
        ui.harmonyMode.value = state.harmonyMode;
        ui.paletteCount.value = state.paletteCount;
        ui.tempShift.value = state.tempShift;
        ui.tempOut.textContent = state.tempShift + '°';
        ui.contrastColor.value = state.contrastColor;
        ui.contrastHex.value = state.contrastColor.replace('#', '').toUpperCase();
        ui.gridView.classList.toggle('hidden', state.viewMode !== 'grid');
        ui.listView.classList.toggle('hidden', state.viewMode !== 'list');
        document.querySelectorAll('#viewTabs button').forEach(b => b.classList.toggle('active', b.dataset.view === state.viewMode));
        document.querySelectorAll('#fmtTabs button').forEach(b => b.classList.toggle('active', b.dataset.fmt === state.fmtMode));
    }

    /* ─── MAIN RENDER ────────────────────────────────────────────── */
    function render() {
        buildPalette();
        ui.harmonyDesc.textContent = HARMONY_DESC[state.harmonyMode] || '';
        const items = getPaletteItems();
        renderStrip(items);
        renderGrid(items);
        renderList(items);
        renderEditor();
        renderPreview();
        renderCompare();
        renderCode();
        renderContrast();
        save();
    }

    /* ─── HSL REAL-TIME PREVIEW ──────────────────────────────────── */
    // Couleur originale avant preview (pour Cancel)
    let hslOriginalHex = null;

    function getHslPreviewHex() {
        return hslToHex(+ui.hueRange.value, +ui.satRange.value, +ui.lightRange.value);
    }

    function updateHslPreview() {
        const hex = getHslPreviewHex();
        ui.hslPreviewSwatch.style.background = hex;
        ui.hslPreviewHex.textContent = hex.toUpperCase();
    }

    function applyHslPreview() {
        const i = parseInt(ui.editIndex.value || 0);
        const previewHex = getHslPreviewHex();
        // Injection temporaire dans palette (sans state)
        palette[i] = previewHex;
        const items = getPaletteItems();
        renderStrip(items);
        renderGrid(items);
        renderList(items);
        renderPreview();
        renderContrast();
        updateHslPreview();
    }

    /* ─── EXPORT ─────────────────────────────────────────────────── */
    function exportImage(type) {
        const canvas = ui.exportCanvas, ctx = canvas.getContext('2d');
        const { width: w, height: h } = canvas;
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
        const pad = 40, top = 70, barH = 120, seg = (w - pad * 2) / palette.length;
        palette.forEach((hex, i) => {
            ctx.fillStyle = hex; ctx.fillRect(pad + i * seg, top, seg, barH);
            ctx.fillStyle = idealText(hex); ctx.font = 'bold 22px monospace'; ctx.textAlign = 'center';
            ctx.fillText(hex.toUpperCase(), pad + i * seg + seg / 2, top + barH + 34);
        });
        ctx.fillStyle = '#000'; ctx.font = 'bold 26px sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(`Generated palette — ${state.harmonyMode}`, pad, 38);

        if (type === 'svg') {
            const s = 1120 / palette.length;
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="300">
      <rect width="100%" height="100%" fill="#fff"/>
      <text x="40" y="36" font-size="26" font-family="sans-serif" font-weight="700">Generated palette — ${state.harmonyMode}</text>
      ${palette.map((hex, i) => `
        <rect x="${40 + i * s}" y="70" width="${s}" height="120" fill="${hex}"/>
        <text x="${40 + i * s + s / 2}" y="222" font-size="22" font-family="monospace" text-anchor="middle" fill="${idealText(hex)}">${hex.toUpperCase()}</text>`
            ).join('')}
    </svg>`;
            return copy(svg, 'SVG copied');
        }

        const mimeType = type === 'jpeg' ? 'image/jpeg' : type === 'webp' ? 'image/webp' : 'image/png';
        canvas.toBlob(async blob => {
            if (!blob) return showToast('Export failed');
            try {
                await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
                showToast(`${type.toUpperCase()} copied`);
            } catch {
                const a = Object.assign(document.createElement('a'), {
                    href: URL.createObjectURL(blob),
                    download: `palette.${type === 'jpeg' ? 'jpg' : type}`
                });
                a.click(); showToast('Downloaded');
            }
        }, mimeType, .95);
    }

    /* ─── EVENTS ─────────────────────────────────────────────────── */
    function bindEvents() {

        // Color picker
        ui.colorPicker.addEventListener('input', () => {
            state.baseHex = ui.colorPicker.value;
            ui.hexInput.value = state.baseHex.replace('#', '').toUpperCase();
            ui.hexInput.classList.remove('invalid');
            render();
        });

        // Hex input
        ui.hexInput.addEventListener('input', debounce(() => {
            const v = ui.hexInput.value.trim().replace('#', '');
            if (/^[0-9a-fA-F]{6}$/.test(v)) {
                state.baseHex = '#' + v;
                ui.colorPicker.value = state.baseHex;
                ui.hexInput.classList.remove('invalid');
                render();
            } else ui.hexInput.classList.add('invalid');
        }, 80));

        // Harmony / count / temp
        ui.harmonyMode.addEventListener('change', () => { state.harmonyMode = ui.harmonyMode.value; render(); });
        ui.paletteCount.addEventListener('change', () => {
            state.paletteCount = clamp(parseInt(ui.paletteCount.value) || 4, 2, 12);
            ui.paletteCount.value = state.paletteCount; render();
        });
        ui.tempShift.addEventListener('input', debounce(() => {
            state.tempShift = parseInt(ui.tempShift.value) || 0;
            ui.tempOut.textContent = state.tempShift + '°'; render();
        }, 40));

        // Random / Reset
        $('btnRandom').addEventListener('click', () => {
            state.baseHex = '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
            syncUI(); render();
        });
        $('btnReset').addEventListener('click', reset);

        // ── HSL sliders : preview temps réel ────────────────────────
        [ui.hueRange, ui.satRange, ui.lightRange].forEach((inp, idx) => {
            const out = [ui.hueOut, ui.satOut, ui.lightOut][idx];
            const suffix = idx === 0 ? '' : '%';
            inp.addEventListener('input', () => {
                out.textContent = inp.value + suffix;
                applyHslPreview();
            });
        });

        // Select couleur à éditer → mémoriser original + sync
        ui.editIndex.addEventListener('change', () => {
            // Annuler toute preview en cours avant de changer de couleur
            buildPalette();
            const items = getPaletteItems();
            renderStrip(items);
            renderGrid(items);
            renderList(items);
            renderPreview();
            renderContrast();
            syncEditor();
            hslOriginalHex = palette[parseInt(ui.editIndex.value || 0)];
        });

        // Apply HSL → confirmer dans state
        $('btnApplyHsl').addEventListener('click', () => {
            const i = parseInt(ui.editIndex.value || 0);
            state.lockedColors[i] = getHslPreviewHex();
            hslOriginalHex = null;
            render();
            showToast('HSL applied & locked');
        });

        // Cancel → revenir à l'état state propre
        $('btnCancelHsl').addEventListener('click', () => {
            buildPalette();
            const items = getPaletteItems();
            renderStrip(items);
            renderGrid(items);
            renderList(items);
            renderPreview();
            renderContrast();
            syncEditor();
            hslOriginalHex = null;
            showToast('HSL reverted');
        });

        // View tabs
        $('viewTabs').addEventListener('click', e => {
            const btn = e.target.closest('button[data-view]'); if (!btn) return;
            state.viewMode = btn.dataset.view;
            document.querySelectorAll('#viewTabs button').forEach(b => b.classList.toggle('active', b.dataset.view === state.viewMode));
            ui.gridView.classList.toggle('hidden', state.viewMode !== 'grid');
            ui.listView.classList.toggle('hidden', state.viewMode !== 'list');
            save();
        });

        // Format tabs
        $('fmtTabs').addEventListener('click', e => {
            const btn = e.target.closest('button[data-fmt]'); if (!btn) return;
            state.fmtMode = btn.dataset.fmt;
            document.querySelectorAll('#fmtTabs button').forEach(b => b.classList.toggle('active', b.dataset.fmt === state.fmtMode));
            renderCode(); save();
        });

        // Copy code
        ui.btnCopy.addEventListener('click', () => {
            copy(ui.codeOutput.value, 'Code copied');
            ui.btnCopy.textContent = '✓'; ui.btnCopy.classList.add('ok');
            setTimeout(() => { ui.btnCopy.textContent = 'Copy'; ui.btnCopy.classList.remove('ok'); }, 1500);
        });

        // Contrast
        ui.contrastColor.addEventListener('input', () => {
            state.contrastColor = ui.contrastColor.value;
            ui.contrastHex.value = state.contrastColor.replace('#', '').toUpperCase();
            renderContrast(); save();
        });
        ui.contrastHex.addEventListener('input', debounce(() => {
            const v = ui.contrastHex.value.replace('#', '');
            if (/^[0-9a-fA-F]{6}$/.test(v)) {
                state.contrastColor = '#' + v;
                ui.contrastColor.value = state.contrastColor;
                renderContrast(); save();
            }
        }, 80));

        // Presets (delegation)
        $('presetsGrid').addEventListener('click', e => {
            const dot = e.target.closest('[data-preset]'); if (!dot) return;
            state.baseHex = dot.dataset.preset; syncUI(); render();
        });

        // Strip : copy + lock (delegation)
        ui.paletteStrip.addEventListener('click', e => {
            const lock = e.target.closest('[data-lock]');
            if (lock) {
                const i = lock.dataset.lock;
                if (state.lockedColors[i]) delete state.lockedColors[i];
                else state.lockedColors[i] = palette[i];
                render(); return;
            }
            const cell = e.target.closest('[data-copy]');
            if (cell) copy(cell.dataset.copy);
        });

        // Grid copy (delegation)
        ui.paletteGrid.addEventListener('click', e => {
            const item = e.target.closest('[data-copy]'); if (item) copy(item.dataset.copy);
        });

        // List copy (delegation)
        ui.paletteList.addEventListener('click', e => {
            const row = e.target.closest('[data-copy]'); if (!row) return;
            copy(row.dataset.copy);
            const badge = document.getElementById('cb' + row.dataset.row);
            if (badge) { badge.classList.add('show'); setTimeout(() => badge.classList.remove('show'), 1200); }
        });

        // Compare
        $('btnSaveCompare').addEventListener('click', () => {
            state.comparePalettes.push({ mode: state.harmonyMode, colors: [...palette] });
            renderCompare(); save(); showToast('Palette saved');
        });
        $('btnClearCompare').addEventListener('click', () => { state.comparePalettes = []; renderCompare(); save(); });
        ui.compareGrid.addEventListener('click', e => {
            const btn = e.target.closest('[data-del]'); if (!btn) return;
            state.comparePalettes.splice(+btn.dataset.del, 1); renderCompare(); save();
        });

        // Export
        document.querySelectorAll('[data-export]').forEach(btn =>
            btn.addEventListener('click', () => exportImage(btn.dataset.export))
        );
    }

    /* ─── PRESETS BUILD ─────────────────────────────────────────── */
    function buildPresets() {
        $('presetsGrid').innerHTML = PRESETS.map(hex =>
            `<div class="palette-dot" style="background:${hex}" data-preset="${hex}" title="${hex}" role="button" tabindex="0" aria-label="Preset ${hex}"></div>`
        ).join('');
    }

    /* ─── INIT ───────────────────────────────────────────────────── */
    buildPresets();
    syncUI();
    bindEvents();
    render();

})();